import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getPatientById, getAppointmentsByPatient,
  getMedicalRecordByAppointment, createMedicalRecord, updateMedicalRecord,
  getReportsByPatient, uploadReport, downloadReport, deleteReport,
  completeAppointment, noShowAppointment,
} from '../api';
import { formatDate, formatTimeRange } from '../utils/format';
import styles from './Page.module.css';
import { useAuth } from '../AuthContext';

export default function PatientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDoctor = user?.role === 'DOCTOR';

  const [patient, setPatient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const [recordModal, setRecordModal] = useState(null);
  const [existingRecord, setExistingRecord] = useState(null);
  const [recordForm, setRecordForm] = useState({ diagnosis: '', prescription: '', notes: '' });
  const [recordLoading, setRecordLoading] = useState(false);
  const [recordMsg, setRecordMsg] = useState('');

  const [uploadFile, setUploadFile] = useState(null);
  const [reportType, setReportType] = useState('');
  const [uploadApptId, setUploadApptId] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');

  useEffect(() => { fetchAll(); }, [id]);

  const today = new Date().toISOString().split('T')[0];

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [patientRes, apptRes, reportRes] = await Promise.all([
        getPatientById(id),
        getAppointmentsByPatient(id),
        getReportsByPatient(id),
      ]);
      setPatient(patientRes.data);
      const appts = isDoctor
        ? apptRes.data.filter(a => a.doctorId === user.doctorId)
        : apptRes.data;
      setAppointments(appts);
      setReports(reportRes.data);
    } catch {}
    setLoading(false);
  };

  const isValidForNotes = (a) =>
    (a.status === 'BOOKED' || a.status === 'COMPLETED') && a.slotDate <= today;

  const canMarkComplete = (a) =>
    a.status === 'BOOKED' && a.slotDate <= today;

  const canMarkNoShow = (a) =>
    a.status === 'BOOKED' && a.slotDate < today;

  const handleMarkComplete = async (apptId) => {
    if (!window.confirm('Mark this appointment as completed?')) return;
    try { await completeAppointment(apptId); fetchAll(); }
    catch { alert('Failed to mark as completed.'); }
  };

  const handleMarkNoShow = async (apptId) => {
    if (!window.confirm('Mark patient as No Show?')) return;
    try { await noShowAppointment(apptId); fetchAll(); }
    catch { alert('Failed to mark as no show.'); }
  };

  const openRecordModal = async (appt) => {
    setRecordModal(appt);
    setRecordMsg('');
    setRecordForm({ diagnosis: '', prescription: '', notes: '' });
    setExistingRecord(null);
    try {
      const res = await getMedicalRecordByAppointment(appt.id);
      setExistingRecord(res.data);
      setRecordForm({
        diagnosis: res.data.diagnosis || '',
        prescription: res.data.prescription || '',
        notes: res.data.notes || '',
      });
    } catch {}
  };

  const handleSaveRecord = async () => {
    setRecordLoading(true);
    setRecordMsg('');
    try {
      if (existingRecord) {
        await updateMedicalRecord(existingRecord.id, {
          ...recordForm,
          recordDate: new Date().toISOString().split('T')[0],
        });
      } else {
        await createMedicalRecord({
          ...recordForm,
          recordDate: new Date().toISOString().split('T')[0],
          patient: { id: parseInt(id) },
          doctor: { id: recordModal.doctorId },
          appointment: { id: recordModal.id },
        });
      }
      setRecordMsg('Saved successfully!');
      setTimeout(() => setRecordModal(null), 1200);
    } catch {
      setRecordMsg('Failed to save. Please try again.');
    }
    setRecordLoading(false);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !reportType) return;
    setUploadLoading(true);
    setUploadMsg('');
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('patientId', id);
      formData.append('reportType', reportType);
      if (uploadApptId) formData.append('appointmentId', uploadApptId);
      await uploadReport(formData);
      setUploadMsg('Report uploaded successfully!');
      setUploadFile(null);
      setReportType('');
      setUploadApptId('');
      fetchAll();
      setTimeout(() => setUploadMsg(''), 3000);
    } catch {
      setUploadMsg('Upload failed. Please try again.');
    }
    setUploadLoading(false);
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Delete this report?')) return;
    await deleteReport(reportId);
    fetchAll();
  };

  const handleDownload = async (report) => {
    try {
      const res = await downloadReport(report.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: report.fileType }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to download report.');
    }
  };

  if (loading) return <p style={{ padding: 32 }}>Loading...</p>;
  if (!patient) return <p style={{ padding: 32 }}>Patient not found.</p>;

  const validAppointmentsForUpload = appointments.filter(
    a => (a.status === 'BOOKED' || a.status === 'COMPLETED') && a.slotDate <= today
  );

  return (
    <div>
      <button onClick={() => navigate('/patients')}
        style={{
          background: 'none', border: 'none', color: '#1a3a6b',
          fontWeight: 600, fontSize: 14, cursor: 'pointer',
          marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6,
        }}>
        &larr; Back to Patients
      </button>

      <h2 className={styles.heading}>Patient Profile</h2>

      {/* Patient Info Card */}
      <div className={styles.card} style={{ marginBottom: 24 }}>
        <h3>Personal Details</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {[
            { label: 'Full Name', value: patient.name },
            { label: 'Phone',     value: patient.phone },
            { label: 'Age',       value: patient.age ? patient.age + ' years' : '-' },
            { label: 'Gender',    value: patient.gender || '-' },
            { label: 'Email',     value: patient.email || '-' },
          ].map((item, i) => (
            <div key={i} style={{ background: '#f4f6fb', borderRadius: 8, padding: '10px 14px' }}>
              <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 3 }}>{item.label}</p>
              <p style={{ fontSize: 15, fontWeight: 600, color: '#1a3a6b' }}>{item.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Appointment History */}
      <div className={styles.card} style={{ marginBottom: 24 }}>
        <h3>
          {isDoctor ? 'My Appointments with this Patient' : 'Appointment History'} ({appointments.length})
        </h3>
        {appointments.length === 0 ? (
          <p style={{ color: '#888', fontSize: 14 }}>
            {isDoctor ? 'No appointments with this patient.' : 'No appointments found.'}
          </p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                {!isDoctor && <th>Doctor</th>}
                {!isDoctor && <th>Specialization</th>}
                <th>Date</th>
                <th>Time</th>
                <th>Status</th>
                <th>Notes</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {appointments.map((a, i) => {
                const isFuture    = a.slotDate > today;
                const isCancelled = a.status === 'CANCELLED';
                const isCompleted = a.status === 'COMPLETED';
                const isNoShow    = a.status === 'NO_SHOW';
                const canAct = !isDoctor || a.doctorId === user.doctorId;

                return (
                  <tr key={a.id}>
                    <td>{i + 1}</td>
                    {!isDoctor && <td><strong>{a.doctorName || '-'}</strong></td>}
                    {!isDoctor && <td>{a.doctorSpecialization || '-'}</td>}
                    <td>{formatDate(a.slotDate)}</td>
                    <td>{formatTimeRange(a.startTime, a.endTime)}</td>

                    <td>
                      <span style={{
                        padding: '3px 10px', borderRadius: 20,
                        fontSize: 12, fontWeight: 700,
                        background: isCancelled ? '#fce4e4' : isNoShow ? '#fff3e0'
                          : isFuture ? '#fff8e1' : isCompleted ? '#e3f2fd' : '#e8f5e9',
                        color: isCancelled ? '#c0272d' : isNoShow ? '#e65100'
                          : isFuture ? '#f57f17' : isCompleted ? '#1565c0' : '#2e7d32',
                      }}>
                        {isCancelled ? 'CANCELLED' : isNoShow ? 'NO SHOW'
                          : isFuture ? 'UPCOMING' : isCompleted ? 'COMPLETED' : 'BOOKED'}
                      </span>
                    </td>

                    <td>
                      <button
                        className={styles.editBtn}
                        onClick={() => (isValidForNotes(a) && canAct) ? openRecordModal(a) : null}
                        disabled={!isValidForNotes(a) || !canAct}
                        style={{
                          opacity: (isValidForNotes(a) && canAct) ? 1 : 0.4,
                          cursor: (isValidForNotes(a) && canAct) ? 'pointer' : 'not-allowed',
                        }}>
                        {isCancelled || isNoShow ? 'N/A' : isFuture ? 'Upcoming' : 'Notes'}
                      </button>
                    </td>

                    <td>
                      {canAct && canMarkNoShow(a) ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          <button className={styles.btn}
                            style={{ fontSize: 12, padding: '4px 10px', width: 'auto' }}
                            onClick={() => handleMarkComplete(a.id)}>
                            ✓ Complete
                          </button>
                          <button onClick={() => handleMarkNoShow(a.id)}
                            style={{
                              fontSize: 12, padding: '4px 10px',
                              background: '#fff3e0', color: '#e65100',
                              border: '1.5px solid #e65100',
                              borderRadius: 6, cursor: 'pointer', fontWeight: 600,
                            }}>
                            ✗ No Show
                          </button>
                        </div>
                      ) : canAct && canMarkComplete(a) ? (
                        <button className={styles.btn}
                          style={{ fontSize: 12, padding: '4px 10px', width: 'auto' }}
                          onClick={() => handleMarkComplete(a.id)}>
                          ✓ Mark Complete
                        </button>
                      ) : isCompleted ? (
                        <span style={{ fontSize: 12, color: '#1565c0', fontWeight: 600 }}>✓ Done</span>
                      ) : isNoShow ? (
                        <span style={{ fontSize: 12, color: '#e65100', fontWeight: 600 }}>✗ No Show</span>
                      ) : (
                        <span style={{ fontSize: 12, color: '#aaa' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Upload Report */}
      <div className={styles.grid}>
        <div className={styles.card} style={{ opacity: validAppointmentsForUpload.length === 0 ? 0.5 : 1 }}>
          <h3>Upload Report</h3>
          {validAppointmentsForUpload.length === 0 && (
            <p style={{ fontSize: 13, color: '#c0272d', marginBottom: 12, fontWeight: 500 }}>
              No valid appointments available to upload reports.
            </p>
          )}
          <form onSubmit={handleUpload}>
            <div className={styles.field}>
              <label>Report Type *</label>
              <select value={reportType} onChange={e => setReportType(e.target.value)}
                required disabled={validAppointmentsForUpload.length === 0}>
                <option value="">Select type</option>
                <option value="Blood Test">Blood Test</option>
                <option value="Urine Test">Urine Test</option>
                <option value="X-Ray">X-Ray</option>
                <option value="MRI">MRI</option>
                <option value="CT Scan">CT Scan</option>
                <option value="ECG">ECG</option>
                <option value="Ultrasound">Ultrasound</option>
                <option value="Prescription">Prescription</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className={styles.field}>
              <label>Link to Appointment (optional)</label>
              <select value={uploadApptId} onChange={e => setUploadApptId(e.target.value)}
                disabled={validAppointmentsForUpload.length === 0}>
                <option value="">None</option>
                {validAppointmentsForUpload.map(a => (
                  <option key={a.id} value={a.id}>
                    {formatDate(a.slotDate)} - Dr. {a.doctorName}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label>Select File *</label>
              <input type="file" accept=".pdf,image/*"
                onChange={e => setUploadFile(e.target.files[0])}
                required disabled={validAppointmentsForUpload.length === 0} />
            </div>
            {uploadMsg && (
              <p style={{
                fontSize: 13, fontWeight: 500, marginBottom: 8,
                color: uploadMsg.includes('success') ? '#2e7d32' : '#c0272d',
              }}>{uploadMsg}</p>
            )}
            <button type="submit" className={styles.btn}
              disabled={uploadLoading || validAppointmentsForUpload.length === 0}>
              {uploadLoading ? 'Uploading...' : 'Upload Report'}
            </button>
          </form>
        </div>

        {/* Reports List */}
        <div className={styles.card}>
          <h3>Uploaded Reports ({reports.length})</h3>
          {reports.length === 0 ? (
            <p style={{ color: '#888', fontSize: 14 }}>No reports uploaded yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {reports.map(r => (
                <div key={r.id} style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', padding: '10px 14px',
                  border: '1.5px solid #e0e4ed', borderRadius: 8, background: '#fafbff',
                }}>
                  <div>
                    <p style={{ fontWeight: 600, color: '#1a3a6b', margin: 0, fontSize: 14 }}>
                      {r.reportType}
                    </p>
                    <p style={{ fontSize: 12, color: '#888', margin: '2px 0 0' }}>
                      {r.fileName} · {formatDate(r.uploadDate)}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className={styles.editBtn} onClick={() => handleDownload(r)}>View</button>
                    {!isDoctor && (
                      <button className={styles.deleteBtn} onClick={() => handleDeleteReport(r.id)}>Delete</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Medical Record Modal */}
      {recordModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: 28,
            width: '100%', maxWidth: 500,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ color: '#1a3a6b', marginBottom: 16, fontSize: 17 }}>Consultation Notes</h3>
            <div style={{
              background: '#e8edf6', borderRadius: 8,
              padding: '10px 14px', marginBottom: 16, fontSize: 13,
            }}>
              <p style={{ margin: '0 0 4px' }}><strong>Doctor:</strong> {recordModal.doctorName}</p>
              <p style={{ margin: 0 }}>
                <strong>Date:</strong> {formatDate(recordModal.slotDate)} at {formatTimeRange(recordModal.startTime, recordModal.endTime)}
              </p>
            </div>

            {[
              { key: 'diagnosis',    label: 'Diagnosis',    placeholder: 'e.g. Hypertension, Type 2 Diabetes', rows: 2 },
              { key: 'prescription', label: 'Prescription', placeholder: 'e.g. Tab Amlodipine 5mg - 1-0-1 for 30 days', rows: 3 },
              { key: 'notes',        label: 'Doctor Notes', placeholder: 'e.g. Patient advised to reduce salt intake', rows: 3 },
            ].map(field => (
              <div key={field.key} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#1a3a6b', marginBottom: 5 }}>
                  {field.label}
                </label>
                <textarea
                  value={recordForm[field.key]}
                  onChange={e => setRecordForm({ ...recordForm, [field.key]: e.target.value })}
                  placeholder={field.placeholder}
                  rows={field.rows}
                  style={{
                    width: '100%', padding: '9px 12px',
                    border: '1.5px solid #e0e4ed', borderRadius: 7,
                    fontSize: 14, outline: 'none', resize: 'vertical',
                    boxSizing: 'border-box',
                  }} />
              </div>
            ))}

            {recordMsg && (
              <p style={{
                fontSize: 13, fontWeight: 500, marginBottom: 10,
                color: recordMsg.includes('success') ? '#2e7d32' : '#c0272d',
              }}>{recordMsg}</p>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleSaveRecord} disabled={recordLoading}
                style={{
                  flex: 1, background: '#c0272d', color: '#fff',
                  border: 'none', borderRadius: 8, padding: '10px',
                  fontSize: 14, fontWeight: 700, cursor: 'pointer',
                }}>
                {recordLoading ? 'Saving...' : existingRecord ? 'Update Notes' : 'Save Notes'}
              </button>
              <button onClick={() => setRecordModal(null)}
                style={{
                  flex: 1, background: 'transparent', color: '#1a3a6b',
                  border: '1.5px solid #1a3a6b', borderRadius: 8,
                  padding: '10px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
